/**
 * prebook-discount-hide.js
 *
 * When any cart item has a restricted product tag (prebook-winter / prebook-fall):
 *   1. Hides the order-level automatic discount line in the cart page + mini cart.
 *   2. Corrects the Total  → items_subtotal_price (after line-item discounts,
 *                                                   before order discounts)
 *   3. Corrects "You saved" → compare-at savings only, sourced from
 *                             /products/{handle}.js variant data (since cart.js
 *                             does not reliably expose compare_at_price).
 *
 * HOW TO INSTALL
 * --------------
 * 1. Shopify Admin → Online Store → Themes → Edit Code → Assets → upload this file.
 * 2. In theme.liquid, just before </body>:
 *
 *      {{ 'prebook-discount-hide.js' | asset_url | script_tag }}
 */

(function () {
  "use strict";

  /* ─────────────────────────────────────────────
     CONFIGURE
  ───────────────────────────────────────────── */

  const RESTRICTED_TAGS = ["prebook-winter", "prebook-fall"];

  const ORDER_DISCOUNT_LINE_SELECTOR = [
    ".cart-recap__price-line--highlight",
    ".mini-cart__recap-price-line--highlight",
  ].join(", ");

  const TOTAL_PRICE_SELECTOR = [
    ".cart-recap__price-line:not(.cart-recap__price-line--highlight) .cart-recap__price-line-price",
    ".mini-cart__recap-price-line:not(.mini-cart__recap-price-line--highlight) span:last-child",
  ].join(", ");

  const SAVED_AMOUNT_SELECTOR = [
    ".cart-recap__amount-saved span",
    ".mini-cart__amount-saved span",
  ].join(", ");

  const SAVED_WRAPPER_SELECTOR = [
    ".cart-recap__amount-saved",
    ".mini-cart__amount-saved",
  ].join(", ");

  /* ─────────────────────────────────────────────
     LOGIC
  ───────────────────────────────────────────── */

  let isActive = false;
  let observer = null;
  let debounceTimer = null;

  function formatMoney(cents, currency) {
    return "$" + (cents / 100).toFixed(2) + " " + currency;
  }

  function formatMoneyShort(cents) {
    return "$" + (cents / 100).toFixed(2);
  }

  /**
   * Fetch cart + all unique products in one pass.
   * Returns:
   *   cart           – raw cart.js object
   *   hasRestricted  – true if any item has a restricted tag
   *   lineItemSavings – total compare-at savings in cents
   */
  async function fetchCartAndProducts() {
    const cart = await fetch("/cart.js").then(function (r) { return r.json(); });

    if (!cart.items || cart.items.length === 0) {
      return { cart: cart, hasRestricted: false, lineItemSavings: 0 };
    }

    const uniqueHandles = [...new Set(cart.items.map(function (i) { return i.handle; }))];

    const products = await Promise.all(
      uniqueHandles.map(function (handle) {
        return fetch("/products/" + handle + ".js").then(function (r) { return r.json(); });
      })
    );

    // Build variant_id → compare_at_price lookup from product data.
    // /products/{handle}.js reliably exposes compare_at_price per variant
    // even when cart.js returns it as undefined.
    var variantCompareAt = {};
    products.forEach(function (product) {
      (product.variants || []).forEach(function (variant) {
        variantCompareAt[variant.id] = variant.compare_at_price || 0;
      });
    });

    // Check restricted tags
    var hasRestricted = products.some(function (product) {
      return (product.tags || []).some(function (tag) {
        return RESTRICTED_TAGS.includes(tag);
      });
    });

    // Calculate compare-at savings: (compare_at - price) × qty per line item
    var lineItemSavings = cart.items.reduce(function (sum, item) {
      var cap = variantCompareAt[item.variant_id] || 0;
      if (cap > item.price) {
        sum += (cap - item.price) * item.quantity;
      }
      return sum;
    }, 0);

    return { cart: cart, hasRestricted: hasRestricted, lineItemSavings: lineItemSavings };
  }

  async function applyCartFix() {
    if (observer) observer.disconnect();

    try {
      var result = await fetchCartAndProducts();
      var cart           = result.cart;
      var subtotal       = cart.items_subtotal_price;
      var lineItemSavings = result.lineItemSavings;

      // 1. Hide order discount lines
      document.querySelectorAll(ORDER_DISCOUNT_LINE_SELECTOR).forEach(function (el) {
        el.style.display = "none";
      });

      // 2. Correct the Total
      document.querySelectorAll(TOTAL_PRICE_SELECTOR).forEach(function (el) {
        el.textContent = formatMoney(subtotal, cart.currency);
      });

      // 3. Correct "You saved" to compare-at savings only
      document.querySelectorAll(SAVED_AMOUNT_SELECTOR).forEach(function (el) {
        el.textContent = formatMoneyShort(lineItemSavings);
      });

      document.querySelectorAll(SAVED_WRAPPER_SELECTOR).forEach(function (el) {
        el.style.display = lineItemSavings > 0 ? "" : "none";
      });

    } catch (err) {
      console.warn("[prebook-discount-hide] applyCartFix error:", err);
    } finally {
      setTimeout(reconnectObserver, 200);
    }
  }

  function reconnectObserver() {
    if (observer && isActive) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  function onMutation() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(applyCartFix, 200);
  }

  function watchForCartUpdates() {
    observer = new MutationObserver(onMutation);
    reconnectObserver();
  }

  async function init() {
    try {
      var result = await fetchCartAndProducts();
      if (result.hasRestricted) {
        isActive = true;
        await applyCartFix();
        watchForCartUpdates();
      }
    } catch (err) {
      console.warn("[prebook-discount-hide] init error:", err);
    }
  }

  init();
  document.addEventListener("cart:refresh", init);
  document.addEventListener("cart:updated", init);

})();